import React, { useEffect, useMemo, useState } from 'react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  Title,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import { Line, Bar } from 'react-chartjs-2';
import { ChartLayout, EquityContent, MethodologyContent, OverviewContent, RelevanceContent } from './ChartLayout';
import { KeyMetric, KeyMetrics } from './KeyMetrics';
import {
  loadHomelessnessBundle,
  buildHomelessnessOverviewNarrative,
  type HomelessnessBundle,
  type HomelessnessChartView,
} from '../../services/homelessnessService';
import { downloadDataAsCSV, formatFilename } from '../../utils/downloadUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, BarElement, Title, Tooltip, Legend, Filler);

export interface HomelessnessSectionProps {
  location: string;
}

const CHART_OPTIONS: { value: HomelessnessChartView; label: string }[] = [
  { value: 'rate', label: 'Rate per 1,000 (trend)' },
  { value: 'gender', label: 'By gender' },
  { value: 'race', label: 'By race / ethnicity' },
  { value: 'age', label: 'By age' },
];

const HomelessnessSection: React.FC<HomelessnessSectionProps> = ({ location }) => {
  const [bundle, setBundle] = useState<HomelessnessBundle | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeView, setActiveView] = useState<string>('overview');
  const [chartView, setChartView] = useState<HomelessnessChartView>('rate');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const b = await loadHomelessnessBundle(location);
        if (!cancelled) setBundle(b);
      } catch {
        if (!cancelled) setBundle(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location]);

  const narrative = useMemo(
    () => (bundle ? buildHomelessnessOverviewNarrative(location, bundle) : ''),
    [location, bundle]
  );

  const lineData = useMemo(() => {
    if (!bundle?.hasCoreData || !bundle.trendYears.length) return null;
    return {
      labels: bundle.trendYears.map(String),
      datasets: [
        {
          label: bundle.countyDisplayName || 'Selected county',
          data: bundle.localRatePer1000,
          borderColor: 'rgb(37, 99, 235)',
          backgroundColor: 'rgba(37, 99, 235, 0.12)',
          fill: true,
          tension: 0.2,
          spanGaps: true,
        },
        {
          label: 'California',
          data: bundle.caRatePer1000,
          borderColor: 'rgb(220, 38, 38)',
          backgroundColor: 'rgba(220, 38, 38, 0.08)',
          fill: true,
          tension: 0.2,
          spanGaps: true,
        },
      ],
    };
  }, [bundle]);

  const lineOptions = useMemo(
    () => ({
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { position: 'top' as const },
        title: {
          display: true,
          text: 'People experiencing homelessness per 1,000 residents',
        },
        tooltip: {
          callbacks: {
            label: (ctx: { dataset: { label?: string }; parsed: { y: number | null } }) => {
              const v = ctx.parsed.y;
              const lab = ctx.dataset.label || '';
              return v != null && !Number.isNaN(v) ? `${lab}: ${v.toFixed(2)} / 1,000` : `${lab}: No data`;
            },
          },
        },
      },
      scales: {
        y: {
          beginAtZero: true,
          title: { display: true, text: 'Per 1,000 residents' },
        },
        x: { title: { display: true, text: 'Year' } },
      },
    }),
    []
  );

  const demoBarData = useMemo(() => {
    if (!bundle) return null;
    const slices =
      chartView === 'gender' ? bundle.latestGender : chartView === 'race' ? bundle.latestRace : bundle.latestAge;
    if (!slices.length) return null;
    const metric: 'rate' | 'count' = slices.some((s) => s.ratePer1000Residents != null) ? 'rate' : 'count';
    const labels = slices.map((s) => s.label);
    const data =
      metric === 'rate'
        ? slices.map((s) => s.ratePer1000Residents ?? 0)
        : slices.map((s) => s.count);
    return {
      metric,
      chartJs: {
        labels,
        datasets: [
          {
            label: metric === 'rate' ? 'Per 1,000 residents (group)' : 'PIT count',
            data,
            backgroundColor: 'rgba(59, 130, 246, 0.75)',
            borderColor: 'rgb(29, 78, 216)',
            borderWidth: 1,
          },
        ],
      },
      slices,
    };
  }, [bundle, chartView]);

  const demoBarOptions = useMemo(() => {
    const title =
      chartView === 'gender'
        ? 'By gender (latest year)'
        : chartView === 'race'
          ? 'By race / ethnicity (latest year)'
          : 'By age (latest year)';
    return {
      indexAxis: 'y' as const,
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        title: { display: true, text: title },
        tooltip: {
          callbacks: {
            label: (ctx: { parsed?: { x?: number }; dataIndex: number }) => {
              if (!demoBarData) return '';
              const s = demoBarData.slices[ctx.dataIndex];
              if (!s) return '';
              if (demoBarData.metric === 'rate') {
                const r = s.ratePer1000Residents;
                return r != null
                  ? `${r.toFixed(2)} per 1,000 (${s.count.toLocaleString()} PIT); ${s.shareOfPitPercent.toFixed(1)}% of local PIT`
                  : `${s.count.toLocaleString()} (${s.shareOfPitPercent.toFixed(1)}% of local PIT)`;
              }
              return `${s.count.toLocaleString()} (${s.shareOfPitPercent.toFixed(1)}% of local PIT)`;
            },
          },
        },
      },
      scales: {
        x: {
          beginAtZero: true,
          title: {
            display: true,
            text: demoBarData?.metric === 'rate' ? 'Per 1,000 residents' : 'Point-in-time count',
          },
          ticks: {
            callback: (v: string | number) =>
              demoBarData?.metric === 'rate' ? Number(v).toFixed(1) : Number(v).toLocaleString(),
          },
        },
      },
    };
  }, [chartView, demoBarData]);

  const keyMetricsEl =
    loading || !bundle ? null : (
      <KeyMetrics>
        {bundle.hasCoreData && bundle.latestYear != null && (
          <KeyMetric value={String(bundle.latestYear)} label="Latest data year" color="#1e3a8a" />
        )}
        {bundle.latestRatePer1000Local != null && (
          <KeyMetric
            value={bundle.latestRatePer1000Local.toFixed(1)}
            label={`${bundle.countyDisplayName} (per 1,000)`}
            color="#1e3a8a"
          />
        )}
        {bundle.latestRatePer1000CA != null && (
          <KeyMetric value={bundle.latestRatePer1000CA.toFixed(1)} label="California (per 1,000)" color="#059669" />
        )}
      </KeyMetrics>
    );

  const chartBody = (() => {
    if (loading) {
      return <p style={{ padding: 24, color: '#6b7280' }}>Loading homelessness data…</p>;
    }
    if (chartView === 'rate') {
      if (!lineData) {
        return (
          <p style={{ padding: 24, color: '#6b7280' }}>
            No county-level homelessness trend found. Data use the HUD point-in-time series (typically county and state).
          </p>
        );
      }
      return (
        <div style={{ height: 360, position: 'relative' }}>
          <Line data={lineData} options={lineOptions} />
        </div>
      );
    }
    if (!demoBarData) {
      return (
        <p style={{ padding: 24, color: '#6b7280' }}>
          No breakdown rows in Supabase for this view (table may be empty or use different geofips).
        </p>
      );
    }
    return (
      <div style={{ height: Math.max(280, demoBarData.slices.length * 36), position: 'relative' }}>
        <Bar data={demoBarData.chartJs} options={demoBarOptions} />
      </div>
    );
  })();

  return (
    <ChartLayout
      scrollAnchorId="chart-homelessness"
      title="Homelessness"
      chartType={chartView === 'rate' ? 'line' : 'bar'}
      keyMetrics={keyMetricsEl}
      chartSelector={{
        options: CHART_OPTIONS.map((o) => ({ value: o.value, label: o.label })),
        value: chartView,
        onChange: (v) => setChartView(v as HomelessnessChartView),
      }}
      chartContent={<div id="homelessness-section-chart">{chartBody}</div>}
      overviewContent={
        <OverviewContent title="Overview" description="Homelessness context for climate, health, and housing equity planning.">
          {bundle?.fetchWarnings?.length ? (
            <p style={{ color: '#b45309', marginBottom: 12 }}>Notes: {bundle.fetchWarnings.join(' ')}</p>
          ) : null}
          <p>{narrative}</p>
          {chartView !== 'rate' && (
            <p style={{ marginTop: 12, fontSize: 14, color: '#4b5563' }}>
              Demographic bars use the <strong>latest shared year</strong> in each breakdown table. Tooltips show each
              group’s share of the county point-in-time total. When ACS population by group is present in the row, the chart
              switches to rate per 1,000 residents for that group.
            </p>
          )}
        </OverviewContent>
      }
      relevanceContent={
        <RelevanceContent
          title="Relevance to climate &amp; resilience"
          description="Extreme heat, smoke, and floods disproportionately affect people without stable housing."
        >
          <div className="methodology-details-simple">
            <h5>Health &amp; emergency response</h5>
            <p>
              PIT counts help size cooling centers, clean-air spaces, and outreach during heat and wildfire smoke events.
              Climate action plans increasingly coordinate with housing and behavioral-health systems (e.g., Homekey-style
              investments).
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Infrastructure &amp; services</h5>
            <p>
              Understanding demographic composition supports equitable siting of services, transit access, and energy
              assistance tied to shelter and permanent supportive housing.
            </p>
          </div>
        </RelevanceContent>
      }
      equityContent={
        <EquityContent
          title="Equity"
          description="Homelessness reflects structural inequities; disaggregation highlights who is overrepresented in PIT counts."
        >
          <div className="methodology-details-simple">
            <h5>Disparate exposure</h5>
            <p>
              Race, gender, and age breakdowns can reveal overrepresentation relative to the general population—signals
              for targeted prevention, rental assistance, and culturally competent services.
            </p>
          </div>
          <div className="methodology-details-simple">
            <h5>Data limits</h5>
            <p>
              PIT is a single-night snapshot and undercounts many experiences of housing instability. Use alongside
              school, healthcare, and justice-system indicators where available.
            </p>
          </div>
        </EquityContent>
      }
      methodologyContent={
        <MethodologyContent
          title="Methods &amp; data sources"
          description="Supabase tables aligned with the equity pipeline (HUD PIT–style county totals and demographic splits)."
          dataSources={[
            '`homelessness` — county & state totals; `percent` as share of general population when present',
            '`homelessness_by_gender`, `homelessness_by_race`, `homelessness_by_age` — PIT counts by dimension',
            'U.S. HUD Annual Homeless Assessment Report (AHAR) / local Continuum of Care point-in-time methodology',
          ]}
          methodology={[
            'City selections resolve to the parent county for the main `homelessness` series (matches legacy API behavior).',
            'Rate per 1,000 residents = `percent` × 1000 when `percent` is a 0–1 fraction; if `percent` is 0–100, divide by 100 first. Otherwise count ÷ population × 1000 when both exist.',
            'California comparison uses state geofips (6) in the same table.',
            'Demographic charts: latest year per table; shares = group count ÷ sum of county PIT counts that year (excluding rows labeled Total).',
            'If optional population fields exist on breakdown rows, horizontal bars show rate per 1,000 for that group.',
          ]}
        />
      }
      activeView={activeView}
      onViewChange={setActiveView}
      showRelevanceEquity={true}
      enableComments={true}
      location={location}
      appendLocationToTitle={true}
      onDownloadPNG={() => {
        const canvas = document.querySelector('#homelessness-section-chart canvas') as HTMLCanvasElement;
        if (canvas) {
          const link = document.createElement('a');
          link.download = `${formatFilename(location, `Homelessness_${chartView}`)}.png`;
          link.href = canvas.toDataURL('image/png');
          link.click();
        }
      }}
      onDownloadCSV={() => {
        if (!bundle?.hasCoreData) return;
        const rows: Record<string, string | number | null>[] = [];
        bundle.trendYears.forEach((y, i) => {
          rows.push({
            location,
            chart: 'rate_trend',
            year: y,
            local_per_1000: bundle.localRatePer1000[i] ?? null,
            california_per_1000: bundle.caRatePer1000[i] ?? null,
          });
        });
        bundle.latestGender.forEach((s) => {
          rows.push({
            location,
            chart: 'gender_latest',
            year: bundle.latestYear,
            label: s.label,
            count: s.count,
            share_of_pit_pct: Math.round(s.shareOfPitPercent * 10) / 10,
            rate_per_1000: s.ratePer1000Residents,
          });
        });
        bundle.latestRace.forEach((s) => {
          rows.push({
            location,
            chart: 'race_latest',
            year: bundle.latestYear,
            label: s.label,
            count: s.count,
            share_of_pit_pct: Math.round(s.shareOfPitPercent * 10) / 10,
            rate_per_1000: s.ratePer1000Residents,
          });
        });
        bundle.latestAge.forEach((s) => {
          rows.push({
            location,
            chart: 'age_latest',
            year: bundle.latestYear,
            label: s.label,
            count: s.count,
            share_of_pit_pct: Math.round(s.shareOfPitPercent * 10) / 10,
            rate_per_1000: s.ratePer1000Residents,
          });
        });
        downloadDataAsCSV(rows, formatFilename(location, 'Homelessness_Data'));
      }}
      pngFilename={formatFilename(location, `Homelessness_${chartView}`)}
      csvFilename={formatFilename(location, 'Homelessness_Data')}
    />
  );
};

export default HomelessnessSection;
